import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2, MessageSquareText } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { smsLink, waLink } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Light Duty Towing",
  "Lockout Service",
  "Battery Jumpstart",
  "Tire Change",
  "Winch-Out & Recovery",
  "Roadside Assistance",
  "Other",
];

const inputCls =
  "w-full bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-zinc-500 outline-none transition-colors duration-300 focus:border-emergency";

const Label = ({ children }) => (
  <span className="mb-1.5 block font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">{children}</span>
);

const QuoteForm = ({ initialService }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    pickup_address: "",
    dropoff_address: "",
    vehicle_year: "",
    vehicle_make: "",
    vehicle_model: "",
    service: initialService || SERVICE_OPTIONS[0],
    reason: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const vehicle = [form.vehicle_year, form.vehicle_make, form.vehicle_model].filter(Boolean).join(" ");
  const quickMsg = `Hi Paliway Towing! I need ${form.service}. Pickup: ${form.pickup_address || "(share location)"}${
    form.dropoff_address ? ` | Drop-off: ${form.dropoff_address}` : ""
  }${vehicle ? ` | Vehicle: ${vehicle}` : ""}. — ${form.name || "Customer"}`;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      toast.success("Request received — dispatch will reach you shortly.");
    } catch {
      toast.error("Could not send right now. Please call or text us instead.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-6" data-testid="quote-form-success">
        <CheckCircle2 className="mx-auto h-12 w-12 text-dispatch" />
        <h3 className="mt-4 font-display text-2xl font-bold uppercase text-white">Request Received</h3>
        <p className="mt-2 text-sm text-zinc-400">
          Dispatch has your details and will contact you at <span className="text-white">{form.phone}</span>.
          Need us faster? Reach us instantly:
        </p>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <a href={smsLink(quickMsg)} data-testid="quote-success-sms-button" className="flex items-center justify-center gap-2 border border-white/20 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:border-emergency hover:text-emergency">
            <MessageSquareText className="h-4 w-4" /> Text Us
          </a>
          <a href={waLink(quickMsg)} target="_blank" rel="noopener noreferrer" data-testid="quote-success-whatsapp-button" className="flex items-center justify-center gap-2 bg-dispatch py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-dispatch-dark">
            <FaWhatsapp className="h-4 w-4" /> WhatsApp
          </a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="quote-request-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Your name</Label>
          <input required value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} data-testid="quote-form-name-input" />
        </div>
        <div>
          <Label>Phone number *</Label>
          <input required type="tel" value={form.phone} onChange={set("phone")} placeholder="(682) 555-1234" className={inputCls} data-testid="quote-form-phone-input" />
        </div>
      </div>
      <div>
        <Label>Pick-up address *</Label>
        <input required value={form.pickup_address} onChange={set("pickup_address")} placeholder="Where is the vehicle now? (street, intersection, or landmark)" className={inputCls} data-testid="quote-form-pickup-input" />
      </div>
      <div>
        <Label>Drop-off address</Label>
        <input value={form.dropoff_address} onChange={set("dropoff_address")} placeholder="Where should we take it? (optional)" className={inputCls} data-testid="quote-form-dropoff-input" />
      </div>
      <div>
        <Label>Vehicle</Label>
        <div className="grid grid-cols-3 gap-3">
          <input value={form.vehicle_year} onChange={set("vehicle_year")} placeholder="Year" className={inputCls} data-testid="quote-form-year-input" />
          <input value={form.vehicle_make} onChange={set("vehicle_make")} placeholder="Make" className={inputCls} data-testid="quote-form-make-input" />
          <input value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="Model" className={inputCls} data-testid="quote-form-model-input" />
        </div>
      </div>
      <div>
        <Label>Service needed</Label>
        <select value={form.service} onChange={set("service")} className={`${inputCls} appearance-none`} data-testid="quote-form-service-select">
          {SERVICE_OPTIONS.map((s) => (
            <option key={s} value={s} className="bg-charcoal">{s}</option>
          ))}
        </select>
      </div>
      <div>
        <Label>Reason for service</Label>
        <textarea value={form.reason} onChange={set("reason")} rows={3} placeholder="What happened? (breakdown, accident, won't start, flat tire...)" className={`${inputCls} resize-none`} data-testid="quote-form-reason-input" />
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="quote-form-submit-button"
        className="flex w-full items-center justify-center gap-2 bg-emergency py-4 font-display text-base font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-emergency-dark disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        {loading ? "Sending..." : "Send Quote Request"}
      </button>
    </form>
  );
};

export default QuoteForm;
